"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef } from "react";

/**
 * Forest Walk entry intro — the portal into the forest.
 *
 *   Phase "code"   — Runes flash in a random unlock sequence.
 *   Phase "accept" — All runes pulse: "code accepted".
 *   Phase "unlock" — Keyhole on the door glows emerald.
 *   Phase "open"   — Door swings open, camera approaches.
 *   Phase "walk"   — Portal scales up as camera passes through the doorway.
 *   Phase "done"   — Overlay unmounts.
 */
interface ForestIntroProps {
  onComplete: () => void;
  sceneReady: boolean;
  onDoorOpen?: () => void;
  onPhaseChange?: (phase: string) => void;
}

const RUNES = ["ᚠ", "ᛗ", "ᚦ", "ᚷ", "ᛚ", "ᚨ", "ᛒ", "ᚱ", "ᛏ"];

// Generate a random unlock code: 12 rune indices (repeats allowed).
const CODE_LENGTH = 12;
const generateCode = () =>
  Array.from({ length: CODE_LENGTH }, () => Math.floor(Math.random() * RUNES.length));

const ForestIntro = ({ onComplete, sceneReady, onDoorOpen, onPhaseChange }: ForestIntroProps) => {
  // The rune/unlock sequence plays for everyone — including users with
  // prefers-reduced-motion. The genuinely motion-sensitive part (the camera
  // fly-through bob/drift) is suppressed separately in CameraRig; the runes
  // are small color/glow pulses and the camera walk runs regardless, so
  // skipping just the runes for reduced-motion was inconsistent (and left the
  // state machine stuck in "code" — the iPad freeze/skip bug).
  const [phase, setPhase] = useState<
    "code" | "accept" | "unlock" | "open" | "walk" | "done"
  >("code");

  // Scale is controlled by phase + Framer Motion transition timing

  // ── Code entry state ──
  const codeRef = useRef<number[]>(generateCode());
  const [codeStep, setCodeStep] = useState(-1); // -1 = not started
  const [activeRune, setActiveRune] = useState<number | null>(null);
  // eslint-disable-next-line react-hooks/refs
  const codeComplete = codeStep >= codeRef.current.length;

  // Start flashing the code once sceneReady fires.
  useEffect(() => {
    if (!sceneReady || phase !== "code") return;

    let step = 0;
    const flashNext = () => {
      if (step >= codeRef.current.length) {
        setCodeStep(step);
        setActiveRune(null);
        return;
      }
      const runeIdx = codeRef.current[step];
      setCodeStep(step);
      setActiveRune(runeIdx);
      step++;
      // Flash duration 160ms, gap between flashes 180ms
      window.setTimeout(() => setActiveRune(null), 160);
      window.setTimeout(flashNext, 340);
    };

    // Small delay before code starts so the user sees the idle runes first
    const startDelay = window.setTimeout(flashNext, 600);
    return () => clearTimeout(startDelay);
  }, [sceneReady, phase]);

  // Report phase changes up to parent
  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  // Phase transitions
  useEffect(() => {
    if (phase !== "code" || !codeComplete) return;
    // Code finished → accept pulse → unlock
    const t1 = setTimeout(() => setPhase("accept"), 300);
    return () => clearTimeout(t1);
  }, [phase, codeComplete]);

  useEffect(() => {
    if (phase !== "accept") return;
    const t = setTimeout(() => setPhase("unlock"), 600);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "unlock") return;
    // Keyhole glows for 2.2s (3 pulses) before door begins to open
    const t = setTimeout(() => {
      setPhase("open");
      onDoorOpen?.();
    }, 2200);
    return () => clearTimeout(t);
  }, [phase, onDoorOpen]);

  useEffect(() => {
    if (phase !== "open") return;
    // Door swings open over 3.5s, then hold before walkthrough
    const t = setTimeout(() => setPhase("walk"), 3500);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "walk") return;
    // Camera walks through the doorway:
    //   0-6s: portal scales 1.4 → 12 (slow start, rapid finish)
    //   6s: overlay unmounts
    const t = setTimeout(() => {
      setPhase("done");
      onComplete();
    }, 6000);
    return () => clearTimeout(t);
  }, [phase, onComplete]);

  // Safety net: if the rune sequence stalls, force the door open after 14s.
  // Anchored to sceneReady (NOT mount): the runes can't start until the scene
  // is ready, and on heavy high-tier devices (e.g. iPad M4, which compiles the
  // full SSAO/GodRays/Bloom pipeline + 280 trees up front) sceneReady can fire
  // several seconds in. A mount-anchored timer burned that budget during the
  // compile freeze and skipped the entire rune/unlock/door-sound sequence.
  useEffect(() => {
    if (phase !== "code" || !sceneReady) return;
    const t = setTimeout(() => setPhase("open"), 14000);
    return () => clearTimeout(t);
  }, [phase, sceneReady]);

  const isAcceptPulse = phase === "accept";

  return (
    <AnimatePresence>
      {phase !== "done" && (
        <motion.div
          className="pointer-events-none absolute inset-0 z-[100]"
          style={{ containerType: "inline-size" }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* The door itself is now a REAL 3D object in the forest scene
              (see WorkshopDoor in ForestScene). This overlay only carries
              the title + rune code, positioned above the 3D door's
              on-screen location. */}

          {/* Rune arch — code entry sequence, arched above the 3D door.
              Sized in cqw (% of the canvas width) so it scales with the 3D
              door on every screen size instead of being a fixed pixel arch. */}
          <motion.div
            className="absolute left-1/2"
            style={{
              top: "4%",
              transform: "translateX(-50%)",
              width: "24cqw",
              height: "11cqw",
            }}
            animate={{ opacity: sceneReady && (phase === "code" || phase === "accept") ? 1 : 0 }}
            transition={{ duration: 0.4 }}
          >
            {RUNES.map((rune, i) => {
              const angle = (i / (RUNES.length - 1)) * Math.PI;
              const radius = 11; // cqw
              const x = -Math.cos(angle) * radius;
              const y = -Math.sin(angle) * radius * 0.55;

              const isFlashing = activeRune === i;
              const isAccept = isAcceptPulse;

              // Brightness logic:
              // - Idle: dim (0.12)
              // - Flashing rune: bright (1.0) with glow
              // - Accept pulse: all bright
              let brightness = 0.12;
              if (isAccept) brightness = 1;
              else if (isFlashing) brightness = 1;

              return (
                <div
                  key={i}
                  className="absolute font-[family-name:var(--font-space-grotesk)]"
                  style={{
                    left: `calc(50% + ${x}cqw)`,
                    top: `calc(100% + ${y}cqw)`,
                    transform: "translate(-50%, -50%)",
                    fontSize: "2.4cqw",
                    fontWeight: 600,
                    lineHeight: 1,
                    color: `rgba(52, 211, 153, ${0.15 + brightness * 0.85})`,
                    textShadow:
                      brightness > 0.5
                        ? `0 0 ${(0.6 + brightness * 1.2).toFixed(2)}cqw rgba(16, 185, 129, ${brightness * 0.9})`
                        : "none",
                    transition: "color 0.12s ease-out, text-shadow 0.12s ease-out",
                  }}
                >
                  {rune}
                </div>
              );
            })}
          </motion.div>

        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ForestIntro;
